import { defineMessages } from 'react-intl.macro';

import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import debounce from 'lodash/debounce';

import { useSelector, useDispatch } from '@tmapy/redux';
import { DangerAlert, InfoAlert, SecondaryBtn, SIZE, WaitingAlert } from '@tmapy/style-guide';

import './CatalogList.scss';

import type { ScrollHistoryState } from '../types';
import { setPageTitle } from '../utils/setPageTitle';
import { actionClearFilter, actionLoadData, actionSetEndpointQuery } from '../actions';
import { CatalogItem } from './CatalogItem';
import { Filters } from './Filters';
import { Footer } from './Footer';
import { ENDPOINT_CATALOG_DATA, QUERY_STATE, REPLACE_STATE_DEBOUNCE_TIMEOUT } from '../constants';

const msg = defineMessages({
  itemsNotFound: {
    id: 'catalogList.itemsNotFound',
    defaultMessage:
      'Hledanému výrazu nebo zapnutému filtru neodpovídá žádný záznam. Zkuste jiná klíčová slova nebo změnte filtr.',
  },
  catalogListCount: {
    id: 'catalogList.count',
    defaultMessage: `{count, plural,
            =0 {}
            one {# záznam}
            few {# záznamy}
            other {# záznamů}
        }`,
  },
  clearFilterBtn: { id: 'catalogList.clearFilterBtn', defaultMessage: 'Zrušit filtr vyhledávání' },
  loading: { id: 'catalogList.loading', defaultMessage: 'Načítám záznamy...' },
  error: {
    id: 'catalogList.error',
    defaultMessage:
      'Je nám líto, záznamy se nepovedlo načíst. Zkuste to, prosím, později. Pokud problém přetrvává, kontaktujte nás.',
  },
});

export const lastCatalogUrlState = {
  state: {} as { scroll?: any },
};

export const CatalogList: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const appConfig = useSelector((state) => state.edas.appData.data.config);
  const items = useSelector((state) => state.edas.catalogData.data);
  const catalogList = useSelector((state) => state.edas.catalogData.data);
  const isFilterOn = useSelector((state) => state.edas.catalogData.isFiltered);
  const catalogDataQueryState = useSelector((state) => state.edas.catalogData.queryState);
  const catalogListCount = catalogList.length;
  const title = `${appConfig.name} | ${appConfig.city}`;

  useEffect(() => {
    setPageTitle(title);
  }, [title]);

  useLayoutEffect(() => {
    const body = window.document.body;
    const href = window.location.href;
    const currentUrl = new URL(href);
    const query: Record<string, string | string[]> = {};
    currentUrl.searchParams.forEach((value, key) => {
      query[key] = key === 'fts' ? value : value.split(',');
    });
    dispatch(actionSetEndpointQuery({ endpoint: ENDPOINT_CATALOG_DATA, query }));
    const scroll: ScrollHistoryState | undefined = window.history.state?.usr?.scroll;

    const onScroll = debounce(() => {
      const top = body.scrollTop;
      const left = body.scrollLeft;
      const scroll = { top, left };
      lastCatalogUrlState.state.scroll = scroll;
      const historyState = window.history.state ?? {};
      window.history.replaceState({ ...historyState, usr: { ...historyState?.usr, scroll } }, '');
    }, REPLACE_STATE_DEBOUNCE_TIMEOUT);

    (
      dispatch(
        actionLoadData({ endpoint: ENDPOINT_CATALOG_DATA, scroll, replaceState: true }),
      ) as any as Promise<void>
    ).then(() => {
      body.addEventListener('scroll', onScroll);
      if (!scroll) {
        onScroll();
      }
    });
    return () => {
      body.removeEventListener('scroll', onScroll);
    };
  }, [dispatch, location]);

  const handleClearFilterBtnClick = useCallback(() => {
    dispatch(actionClearFilter());
  }, [dispatch]);

  return (
    <div className='e-catalog'>
      <div className='e-catalog--inner e-u-block'>
        <div className='e-catalog--filter sg-a-pt-4 sg-a-pb-4'>
          <Filters />
        </div>
        <div className='e-catalog--list sg-a-d-f sg-a-fd-c sg-a-pt-4'>
          {catalogDataQueryState === QUERY_STATE.WAITING && (
            <WaitingAlert>
              <FormattedMessage {...msg.loading} />
            </WaitingAlert>
          )}
          {catalogDataQueryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...msg.error} />
            </DangerAlert>
          )}
          {catalogDataQueryState === QUERY_STATE.SUCCESS && (
            <div>
              <nav className='sg-a-d-f sg-a-ai-b sg-a-cg-2 sg-a-pb-1 sg-u-bb sg-u-type-xsm'>
                {!!catalogListCount && (
                  <FormattedMessage
                    {...msg.catalogListCount}
                    values={{ count: catalogListCount }}
                  />
                )}
                <div className={isFilterOn ? 'sg-a-v-v' : 'sg-a-v-h'}>
                  <SecondaryBtn isDanger size={SIZE.SML} onClick={handleClearFilterBtnClick}>
                    <FormattedMessage {...msg.clearFilterBtn} />
                  </SecondaryBtn>
                </div>
              </nav>

              {catalogList.length > 0 ? (
                <ul>
                  {items.map((item) => (
                    <li key={item.uuid}>
                      <CatalogItem item={item} />
                    </li>
                  ))}
                </ul>
              ) : (
                <div className='sg-a-mt-2 sg-a-mb-2'>
                  <InfoAlert>
                    <FormattedMessage {...msg.itemsNotFound} />
                  </InfoAlert>
                </div>
              )}
            </div>
          )}
          <div className='sg-a-mt-a'>
            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
};
